import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Boxes, Loader2 } from "lucide-react";
import type { OrderLineRecord, OrderRecord } from "@/types";
import type { StockUnitRecord } from "@/types/inventory";
import { StockPickerDialog } from "@/components/inventory/StockPickerDialog";

interface Props {
  order: OrderRecord | null;
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
}

const num = (v: number | string | undefined | null) => Number(v || 0);

interface ParsedRoll {
  roll_no: string;
  quantity?: number;
  net_weight?: number;
  gross_weight?: number;
}

/**
 * Roll list pasted from Excel or typed, one roll per row: roll number, then length / quantity, net kg and
 * gross kg (any that are known, in that order), separated by tabs, commas or spaces.
 */
function parseRolls(text: string): { rolls: ParsedRoll[]; problem: string | null } {
  const rolls: ParsedRoll[] = [];
  const rows = text.split(/\r?\n/).map((r) => r.trim()).filter(Boolean);
  for (const [i, row] of rows.entries()) {
    const parts = row.split(/[\t,;]+|\s{1,}/).map((p) => p.trim()).filter(Boolean);
    const [no, ...nums] = parts;
    if (!no) continue;
    if (/^(roll|piece|bale|no\.?)$/i.test(no) && i === 0) continue; // a header row pasted with the data
    const values = nums.map(Number);
    if (values.some((v) => !isFinite(v) || v < 0)) return { rolls, problem: `Row ${i + 1}: "${row}" has a value that isn't a number` };
    rolls.push({ roll_no: no, quantity: values[0], net_weight: values[1], gross_weight: values[2] });
  }
  return { rolls, problem: null };
}

/** Send part or all of an order: each line starts at what is still pending, and may be reduced to send less now */
export function DispatchDialog({ order, open, onClose, onSaved }: Props) {
  const { success, error } = useToast();
  const [form, setForm] = useState({ dispatch_date: "", transporter: "", lr_number: "", vehicle_number: "", packages: "", invoice_number: "", eway: "", eway_until: "", expected: "", notes: "" });
  const [qty, setQty] = useState<Record<string, string>>({});
  const [pk, setPk] = useState<Record<string, string>>({});
  const [lot, setLot] = useState<Record<string, string>>({});
  const [rollText, setRollText] = useState<Record<string, string>>({});
  const [rollsOpen, setRollsOpen] = useState<Record<string, boolean>>({});
  // Rolls picked from stock for a line; they decide that line's quantity, roll list and lot
  const [picked, setPicked] = useState<Record<string, StockUnitRecord[]>>({});
  const [pickLine, setPickLine] = useState<OrderLineRecord | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !order) return;
    setForm({ dispatch_date: new Date().toISOString().slice(0, 10), transporter: "", lr_number: "", vehicle_number: "", packages: "", invoice_number: "", eway: "", eway_until: "", expected: "", notes: "" });
    setQty(Object.fromEntries((order.lines || []).map((l) => [l.id, num(l.pending_qty) > 0 ? String(num(l.pending_qty)) : ""])));
    setPk({});
    setLot({});
    setRollText({});
    setRollsOpen({});
    setPicked({});
    setPickLine(null);
  }, [open, order?.id]);

  if (!order) return null;
  const lines = order.lines || [];

  const applyPick = (line: OrderLineRecord, units: StockUnitRecord[]) => {
    setPicked({ ...picked, [line.id]: units });
    setQty({ ...qty, [line.id]: String(Math.round(units.reduce((a, u) => a + Number(u.quantity), 0) * 1000) / 1000) });
  };
  const clearPick = (line: OrderLineRecord) => {
    const next = { ...picked };
    delete next[line.id];
    setPicked(next);
    setQty({ ...qty, [line.id]: num(line.pending_qty) > 0 ? String(num(line.pending_qty)) : "" });
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.transporter.trim()) return error("Validation Error", "Enter the transporter (or Own vehicle / Hand delivery)");
    const chosen = lines.filter((l) => num(qty[l.id]) > 0);
    if (!chosen.length) return error("Validation Error", "Enter a quantity for at least one line");
    const parsed: Record<string, ParsedRoll[]> = {};
    for (const l of chosen) {
      if (num(qty[l.id]) > num(l.max_dispatch_qty) + 0.0005) {
        return error("Validation Error", `${l.product_name}: at most ${num(l.max_dispatch_qty)} ${l.unit} can still be sent`);
      }
      if (!picked[l.id]?.length && rollText[l.id]?.trim()) {
        const { rolls, problem } = parseRolls(rollText[l.id]!);
        if (problem) return error("Check the roll list", `${l.product_name}: ${problem}`);
        const withQty = rolls.filter((r) => r.quantity != null).length;
        if (withQty && withQty !== rolls.length) return error("Check the roll list", `${l.product_name}: give the length for every roll, or for none`);
        if (withQty) {
          const sum = Math.round(rolls.reduce((a, r) => a + (r.quantity || 0), 0) * 1000) / 1000;
          if (Math.abs(sum - num(qty[l.id])) > 0.01) return error("Check the roll list", `${l.product_name}: the rolls add up to ${sum} but you are sending ${num(qty[l.id])}`);
        }
        parsed[l.id] = rolls;
      }
    }
    setSaving(true);
    try {
      await api.post(`/api/SalesOrder/Dispatch/${order.id}`, {
        dispatch_date: form.dispatch_date || undefined,
        transporter: form.transporter.trim(),
        lr_number: form.lr_number.trim() || undefined,
        vehicle_number: form.vehicle_number.trim() || undefined,
        packages: form.packages === "" ? undefined : Math.round(Number(form.packages)),
        invoice_number: form.invoice_number.trim() || undefined,
        eway_bill_number: form.eway.trim() || undefined,
        eway_valid_until: form.eway.trim() ? form.eway_until || undefined : undefined,
        expected_delivery: form.expected || undefined,
        notes: form.notes.trim() || undefined,
        lines: chosen.map((l) => ({
          order_line_id: l.id,
          quantity: num(qty[l.id]),
          packages: picked[l.id]?.length || parsed[l.id] ? undefined : pk[l.id] ? Math.round(Number(pk[l.id])) : undefined,
          lot_number: picked[l.id]?.length ? undefined : lot[l.id]?.trim() || undefined,
          rolls: parsed[l.id],
          unit_ids: picked[l.id]?.length ? picked[l.id]!.map((u) => u.id) : undefined,
        })),
      });
      success("Dispatch recorded", "The order's pending quantities are updated");
      onSaved();
      onClose();
    } catch (err: any) {
      error("Could not record the dispatch", err.message || "Please try again");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !saving && onClose()}>
      <DialogContent className="sm:max-w-2xl md:max-w-3xl">
        <DialogHeader>
          <DialogTitle>Dispatch against {order.order_number}</DialogTitle>
          <DialogDescription>Send part or all of the order. Each line may go up to its ordered quantity plus the tolerance.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 py-2 text-sm">
          <div className="rounded-lg border border-border overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-muted/40 text-muted-foreground">
                <tr>
                  <th className="text-left font-semibold px-3 py-2">Item</th>
                  <th className="text-right font-semibold px-2 py-2">Ordered</th>
                  <th className="text-right font-semibold px-2 py-2">Sent</th>
                  <th className="text-right font-semibold px-2 py-2">Pending</th>
                  <th className="text-right font-semibold px-2 py-2 w-28">Send now</th>
                  <th className="text-right font-semibold px-2 py-2 w-20">Rolls</th>
                  <th className="text-left font-semibold px-2 py-2 w-28">Lot / batch</th>
                </tr>
              </thead>
              <tbody>
                {lines.map((l) => {
                  const fromStock = picked[l.id] || [];
                  const lotsPicked = [...new Set(fromStock.map((u) => u.lot?.lot_number).filter(Boolean))];
                  const pr = !fromStock.length && rollText[l.id]?.trim() ? parseRolls(rollText[l.id]!) : null;
                  const rollQty = pr ? Math.round(pr.rolls.reduce((a, r) => a + (r.quantity || 0), 0) * 1000) / 1000 : 0;
                  const rollNet = pr ? Math.round(pr.rolls.reduce((a, r) => a + (r.net_weight || 0), 0) * 1000) / 1000 : 0;
                  return (
                  <React.Fragment key={l.id}>
                  <tr className="border-t align-top">
                    <td className="px-3 py-2">
                      <div className="font-medium text-sm">{l.product_name}</div>
                      <div className="text-[11px] text-muted-foreground">
                        {l.variant_name ? `${l.variant_name} · ` : ""}
                        {l.tolerance_pct ? `±${num(l.tolerance_pct)}% · up to ${num(l.max_dispatch_qty)} ${l.unit} more` : `${l.unit}`}
                      </div>
                    </td>
                    <td className="px-2 py-2 text-right">{num(l.quantity)} {l.unit}</td>
                    <td className="px-2 py-2 text-right">{num(l.dispatched_qty)}</td>
                    <td className="px-2 py-2 text-right font-medium">{num(l.pending_qty)}</td>
                    <td className="px-2 py-2">
                      <Input type="number" min={0} step="any" value={qty[l.id] ?? ""} onChange={(e) => setQty({ ...qty, [l.id]: e.target.value })} readOnly={fromStock.length > 0} className={`h-8 text-xs text-right ${fromStock.length ? "bg-muted/50" : ""}`} />
                    </td>
                    <td className="px-2 py-2">
                      {fromStock.length ? (
                        <div className="h-8 flex items-center justify-end text-xs font-medium">{fromStock.length}</div>
                      ) : pr ? (
                        <div className="h-8 flex items-center justify-end text-xs font-medium">{pr.rolls.length}</div>
                      ) : (
                        <Input type="number" min={0} step="1" value={pk[l.id] ?? ""} onChange={(e) => setPk({ ...pk, [l.id]: e.target.value })} className="h-8 text-xs text-right" />
                      )}
                    </td>
                    <td className="px-2 py-2">
                      {fromStock.length ? (
                        <div className="space-y-1">
                          <div className="text-xs font-medium">{lotsPicked.length ? `Lot ${lotsPicked.join(", ")}` : "From stock"}</div>
                          {lotsPicked.length > 1 && <div className="text-[11px] text-amber-600">{lotsPicked.length} lots: shade may vary</div>}
                          <div className="flex gap-2 text-[11px]">
                            <button type="button" className="text-primary hover:underline" onClick={() => setPickLine(l)}>Change</button>
                            <button type="button" className="text-muted-foreground hover:underline" onClick={() => clearPick(l)}>Clear</button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <Input value={lot[l.id] ?? ""} onChange={(e) => setLot({ ...lot, [l.id]: e.target.value })} className="h-8 text-xs" placeholder="Lot no." />
                          <button type="button" className="text-[11px] text-primary hover:underline mt-1" onClick={() => setRollsOpen({ ...rollsOpen, [l.id]: !rollsOpen[l.id] })}>
                            {rollsOpen[l.id] ? "Hide roll list" : pr ? `Roll list (${pr.rolls.length})` : "Add roll list"}
                          </button>
                          {num(l.pending_qty) > 0 && l.product_id && (
                            <button type="button" className="flex items-center gap-1 text-[11px] text-primary hover:underline mt-1" onClick={() => setPickLine(l)}>
                              <Boxes className="size-3" /> Pick from stock{l.reserved_units ? ` (${l.reserved_units} held)` : ""}
                            </button>
                          )}
                        </>
                      )}
                    </td>
                  </tr>
                  {rollsOpen[l.id] && !fromStock.length && (
                    <tr className="bg-muted/20">
                      <td colSpan={7} className="px-3 py-2 space-y-1">
                        <p className="text-[11px] text-muted-foreground">
                          One roll per row: roll no., then length / quantity in {l.unit}, net kg and gross kg (any you know). Paste straight from Excel. The lot in the box above applies to all of them.
                        </p>
                        <Textarea rows={4} value={rollText[l.id] ?? ""} onChange={(e) => setRollText({ ...rollText, [l.id]: e.target.value })} className="text-xs font-mono" placeholder={"R1\t52.5\t11.2\t11.6\nR2\t49\t10.5\t10.9"} />
                        {pr && (
                          <p className={`text-[11px] ${pr.problem || (rollQty && Math.abs(rollQty - num(qty[l.id])) > 0.01) ? "text-red-600" : "text-muted-foreground"}`}>
                            {pr.problem ||
                              `${pr.rolls.length} rolls${rollQty ? ` · ${rollQty} ${l.unit}${Math.abs(rollQty - num(qty[l.id])) > 0.01 ? ` (sending ${num(qty[l.id])})` : ""}` : ""}${rollNet ? ` · net ${rollNet} kg` : ""}`}
                          </p>
                        )}
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="space-y-1.5">
              <Label>Transporter *</Label>
              <Input value={form.transporter} onChange={(e) => setForm({ ...form, transporter: e.target.value })} placeholder="VRL, Own vehicle, Hand delivery..." />
            </div>
            <div className="space-y-1.5">
              <Label>LR number</Label>
              <Input value={form.lr_number} onChange={(e) => setForm({ ...form, lr_number: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Vehicle number</Label>
              <Input value={form.vehicle_number} onChange={(e) => setForm({ ...form, vehicle_number: e.target.value })} />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="space-y-1.5">
              <Label>Dispatch date</Label>
              <Input type="date" value={form.dispatch_date} onChange={(e) => setForm({ ...form, dispatch_date: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Total rolls / bales</Label>
              <Input type="number" min={0} step="1" value={form.packages} onChange={(e) => setForm({ ...form, packages: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Invoice number</Label>
              <Input value={form.invoice_number} onChange={(e) => setForm({ ...form, invoice_number: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Expected delivery</Label>
              <Input type="date" value={form.expected} onChange={(e) => setForm({ ...form, expected: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>E-way bill no.</Label>
              <Input inputMode="numeric" maxLength={12} value={form.eway} onChange={(e) => setForm({ ...form, eway: e.target.value.replace(/\D/g, "") })} placeholder="Goods over ₹50,000" className="font-mono" />
            </div>
            <div className="space-y-1.5">
              <Label>E-way valid until</Label>
              <Input type="date" value={form.eway_until} disabled={!form.eway} onChange={(e) => setForm({ ...form, eway_until: e.target.value })} />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Notes</Label>
            <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? <Loader2 className="size-4 animate-spin mr-1.5" /> : null}
              Record dispatch
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
      <StockPickerDialog
        open={!!pickLine}
        mode="pick"
        line={pickLine ? { id: pickLine.id, product_name: pickLine.product_name, variant_name: pickLine.variant_name, unit: pickLine.unit, needed: num(pickLine.pending_qty), max: num(pickLine.max_dispatch_qty) } : null}
        initialSelected={pickLine ? picked[pickLine.id]?.map((u) => u.id) : undefined}
        onClose={() => setPickLine(null)}
        onPicked={(units) => pickLine && applyPick(pickLine, units)}
      />
    </Dialog>
  );
}
